import { ReactNode } from 'react';
import {
  ControllerRenderProps,
  FieldError,
  FieldPath,
  FieldPathValue,
  FieldValues,
  RegisterOptions,
  useController,
} from 'react-hook-form';
import styled from 'styled-components';

import { InputContainer, InputContainerProps } from '@creditornot/cb-form';
import { InputAlert } from '@creditornot/cb-input';

import { useFormatMessage } from '../../hooks/useFormatMessage';

type RenderInputProps = Pick<
  ControllerRenderProps,
  'value' | 'onChange' | 'onBlur' | 'name' | 'ref'
> & {
  'aria-errormessage'?: string;
  'error'?: FieldError;
  'invalid': boolean;
  'id': string;
};

type FormFieldProps<TFieldValues extends FieldValues> = {
  name: FieldPath<TFieldValues>;
  rules?: Omit<
    RegisterOptions<TFieldValues, FieldPath<TFieldValues>>,
    'valueAsNumber' | 'valueAsDate' | 'setValueAs' | 'disabled'
  >;
  label?: string;
  hint?: string;
  defaultValue?: FieldPathValue<TFieldValues, FieldPath<TFieldValues>>;
  className?: string;
  style?: React.CSSProperties;
  optional?: boolean;
  withError?: boolean;
  renderInput: (props: RenderInputProps) => ReactNode;
} & Pick<InputContainerProps, 'characterCount'>;

const StyledInputAlert = styled(InputAlert)`
  margin-top: 8px;
`;

/**
 * For a checkable input, map the value to the checked attribute, `checked={value}`.
 *
 * If you are using a Select or MultiSelect as you input, make sure to map the ref to selectRef.
 * @example
 * renderInput={({ref, ...fieldProps}) => <Select {...fieldProps} selectRef={ref} />}
 */
export function FormField<TFieldValues extends FieldValues>({
  name,
  rules,
  label,
  hint,
  defaultValue,
  className,
  style,
  optional,
  characterCount,
  renderInput,
  withError = true,
}: FormFieldProps<TFieldValues>) {
  const {
    field: { value, ref, onBlur, onChange },
    fieldState: { error },
  } = useController({ name, rules, defaultValue });

  const format = useFormatMessage();

  const optionalLabel = format('common.optional').toLowerCase();
  const labelText = optional ? `${label} (${optionalLabel})` : label;
  const showError = withError && !!error;
  const errorId = `${name}-error`;

  return (
    <InputContainer
      label={labelText}
      labelHtmlFor={name}
      hint={hint}
      className={className}
      style={style}
      characterCount={characterCount}
    >
      {renderInput({
        name,
        'id': name,
        ref,
        onBlur,
        'onChange': (value: any) => onChange(value ?? null),
        value,
        error,
        'invalid': Boolean(error),
        'aria-errormessage': showError ? errorId : undefined,
      })}
      {showError && (
        <StyledInputAlert id={errorId} variant="error">
          {error.message}
        </StyledInputAlert>
      )}
    </InputContainer>
  );
}
